import { QuestionTypeEnum } from '@doltech/utils/lib/constants';
import { observer } from 'mobx-react-lite';
import * as React from 'react';

import { ERROR_NOT_SUPPORTED } from '../../question-data-model/constants';
import matchingQuestionMappingHelper from '../helper/matchingQuestionMapping.helper';
import { TestContentLayout, TestContentLayoutProps } from '../layouts/TestContentLayout';
import { useTestResultStore } from '../model/test-result.hook';
import { ResultReadingTestSectionInstance } from '../model/TestSection/result-reading-test-section.store';
import { CompletionNoHintAnswerResult } from './questions/CompletionNoHintAnswerResult';
import { DiagramLabelCompletionQuestionResult } from './questions/DiagramLabelCompletionResult';
import { DragInQuestionResult } from './questions/DragInQuestionResult';
import { DragOutQuestionResult } from './questions/DragOutQuestionResult';
import { FLowChartComplexResult } from './questions/FlowChartComplexResult';
import { FlowChartQuestionResult } from './questions/FlowChartQuestionResult';
import { MatchingEndingQuestionResult } from './questions/MatchingEndingQuestionResult';
import { MatchingFeatureNameQuestionResult } from './questions/MatchingFeatureNameQuestionResult';
import { MatchingHeadingQuestionResult } from './questions/MatchingHeadingQuestionResult';
import { MatchingParagraphQuestionResult } from './questions/MatchingParagraphQuestionResult';
import { MultipleQuestionResult } from './questions/MultipleQuestionResult';
import { SentenceCompleteQuestionResult } from './questions/SentenceCompleteQuestionResult';
import ShortAnswerResult from './questions/ShortAnswerResult';
import { SingleQuestionResult } from './questions/SingleQuestionResult';
import { TableCompletionQuestionResult } from './questions/TableCompletionQuestionResult';
import { TFNQuestionResult } from './questions/TFNQuestionResult';
import { YNNQuestionResult } from './questions/YNNQuestionResult';

interface TestContentProps extends TestContentLayoutProps {}

const CurrentQuestionComp = () => {
  const store = useTestResultStore();
  const { goToQuestion } = store;

  if (store.currentQuestion) {
    const { no, uuid, type } = store.currentQuestion;
    const defaultProps = {
      key: `${no}${uuid}`,
      goToQuestion,
      testMode: store.testMode,
    };

    const currentSection = store.getCurrentTestSection() as ResultReadingTestSectionInstance;

    const questionsInGroup = currentSection.getQuestionsInGroup(store.currentGroupIndex);
    const matchingQuestionsInGroup = () =>
      questionsInGroup.map((question) =>
        matchingQuestionMappingHelper(type, question, currentSection.getMarkedBy())
      );

    if (type) {
      switch (type) {
        case QuestionTypeEnum.SINGLE_ANSWER:
          return (
            <SingleQuestionResult
              data={store.currentQuestion}
              questionsInGroup={questionsInGroup}
              {...defaultProps}
            />
          );

        case QuestionTypeEnum.TFN_ANSWER:
          return (
            <TFNQuestionResult
              data={store.currentQuestion}
              questionsInGroup={questionsInGroup}
              {...defaultProps}
            />
          );

        case QuestionTypeEnum.YNN_ANSWER:
          return (
            <YNNQuestionResult
              data={store.currentQuestion}
              questionsInGroup={questionsInGroup}
              {...defaultProps}
            />
          );

        case QuestionTypeEnum.MULTIPLE_ANSWER:
          return (
            <MultipleQuestionResult
              data={store.currentQuestion}
              questionsInGroup={questionsInGroup}
              {...defaultProps}
            />
          );

        case QuestionTypeEnum.MATCHING_PARAGRAPH:
          return (
            <MatchingParagraphQuestionResult
              data={store.currentQuestion}
              markedBy={currentSection.getMarkedBy()}
              questionsInGroup={matchingQuestionsInGroup()}
              {...defaultProps}
            />
          );

        case QuestionTypeEnum.MATCHING_HEADING:
          return (
            <MatchingHeadingQuestionResult
              data={store.currentQuestion}
              markedBy={currentSection.getMarkedBy()}
              questionsInGroup={matchingQuestionsInGroup()}
              {...defaultProps}
            />
          );

        case QuestionTypeEnum.MATCHING_ENDING:
          return (
            <MatchingEndingQuestionResult
              data={store.currentQuestion}
              questionsInGroup={matchingQuestionsInGroup()}
              {...defaultProps}
            />
          );

        case QuestionTypeEnum.MATCHING_FEATURE_NAME:
          return (
            <MatchingFeatureNameQuestionResult
              data={store.currentQuestion}
              questionsInGroup={matchingQuestionsInGroup()}
              {...defaultProps}
            />
          );

        case QuestionTypeEnum.SENTENCE_COMPLETE:
          return <SentenceCompleteQuestionResult data={store.currentQuestion} {...defaultProps} />;

        case QuestionTypeEnum.SHORT_ANSWER:
          return <ShortAnswerResult data={store.currentQuestion} {...defaultProps} />;

        case QuestionTypeEnum.NOTE_COMPLETION_WITH_HINT:
        case QuestionTypeEnum.NOTE_COMPLETION_NO_HINT:
          return <CompletionNoHintAnswerResult data={store.currentQuestion} {...defaultProps} />;

        case QuestionTypeEnum.FLOWCHART_ANSWER:
          return <FlowChartQuestionResult data={store.currentQuestion} {...defaultProps} />;

        case QuestionTypeEnum.DIAGRAM_LABEL_COMPLETION:
          return (
            <DiagramLabelCompletionQuestionResult {...defaultProps} data={store.currentQuestion} />
          );

        case QuestionTypeEnum.TABLE_COMPLETION:
          return <TableCompletionQuestionResult {...defaultProps} data={store.currentQuestion} />;

        case QuestionTypeEnum.DRAG_IN:
          return <DragInQuestionResult {...defaultProps} data={store.currentQuestion} />;

        case QuestionTypeEnum.DRAG_OUT:
          return <DragOutQuestionResult {...defaultProps} data={store.currentQuestion} />;

        case QuestionTypeEnum.FLOW_CHART_COMPLEX:
          return <FLowChartComplexResult {...defaultProps} data={store.currentQuestion} />;

        default:
          return (
            <div>
              {type} {ERROR_NOT_SUPPORTED}
            </div>
          );
      }
    }
  }
  return null;
};

const CurrentQuestion = observer(CurrentQuestionComp as React.FC);

export const TestContent = observer((props: TestContentProps) => {
  return (
    <TestContentLayout {...props}>
      <CurrentQuestion />
    </TestContentLayout>
  );
});
